// Generic, metadata-driven table. Presentational only — all data/sort/paging state
// is owned by the caller (useReportData). No ZTAI-specific imports so it can be
// lifted into an open-source package.
import React, { useState } from 'react';
import { Icon } from '../components/ui';
import { renderCell } from './formatters';
import type { ReportColumn, ReportData, ReportMeta, SortDir } from './types';
import { useTranslation } from 'react-i18next';

interface Props {
  meta: ReportMeta;
  data: ReportData | null;
  loading: boolean;
  sort?: string;
  dir?: SortDir;
  onSort: (key: string) => void;
  onPage: (page: number) => void;
  onSearch: (q: string) => void;
}

const gridColumns = (cols: ReportColumn[]) => cols.filter((c) => c.visible !== false && !c.isDetail);
const detailColumns = (cols: ReportColumn[]) => cols.filter((c) => c.isDetail);

const SortCaret: React.FC<{ active: boolean; dir?: SortDir }> = ({ active, dir }) => (
  <span className="report-sort-caret" aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

const ReportTable: React.FC<Props> = ({ meta, data, loading, sort, dir, onSort, onPage, onSearch }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number | null>(null);
  const cols = gridColumns(meta.columns);
  const details = detailColumns(meta.columns);
  const rows = data?.rows || [];
  const pageSize = meta.pageSize || 25;
  const page = data?.page || 1;
  const total = data?.total;
  const hasMore = data?.hasMore;
  const totalPages = total != null ? Math.max(1, Math.ceil(total / pageSize)) : undefined;

  return (
    <div className="report-table">
      <div className="report-toolbar">
        <div className="report-search-wrap">
          <Icon name="search" size={15} />
          <input
            className="ent-input report-search"
            type="search"
            placeholder={t('reports.reportTable.search')}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        {loading && <span className="report-loading">{t('reports.reportTable.loading')}</span>}
      </div>

      {data?.truncated && (
        <div className="report-warning">{t('reports.reportTable.showingTheFirstRowsResultsWere', { v0: rows.length })}</div>
      )}

      <div className="ent-table-wrap">
        <table className="ent-table">
          <thead>
            <tr>
              {details.length > 0 && <th style={{ width: 32 }} />}
              {cols.map((c) => (
                <th
                  key={c.key}
                  style={{ textAlign: c.align || 'left', width: c.width, cursor: c.sortable ? 'pointer' : 'default' }}
                  onClick={() => c.sortable && onSort(c.key)}
                >
                  {c.label}
                  {c.sortable && <SortCaret active={sort === c.key} dir={sort === c.key ? dir : undefined} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={cols.length + (details.length ? 1 : 0)} className="report-empty">{t('reports.reportTable.loading')}</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={cols.length + (details.length ? 1 : 0)} className="report-empty">{t('reports.reportTable.noData')}</td></tr>
            )}
            {rows.map((row, i) => (
              <React.Fragment key={i}>
                <tr
                  onClick={() => details.length && setExpanded(expanded === i ? null : i)}
                  style={{ cursor: details.length ? 'pointer' : 'default' }}
                >
                  {details.length > 0 && (
                    <td className="report-expand">{expanded === i ? '▾' : '▸'}</td>
                  )}
                  {cols.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || 'left' }}>{renderCell(c, row[c.key])}</td>
                  ))}
                </tr>
                {details.length > 0 && expanded === i && (
                  <tr className="report-detail-row">
                    <td />
                    <td colSpan={cols.length}>
                      <dl className="report-detail">
                        {details.map((c) => (
                          <React.Fragment key={c.key}>
                            <dt>{c.label}</dt>
                            <dd>{renderCell(c, row[c.key])}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {data?.aggregate && rows.length > 0 && (
            <tfoot>
              <tr className="report-agg-row">
                {details.length > 0 && <td />}
                {cols.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align || 'left' }}>{renderCell(c, data.aggregate![c.key])}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="report-pagination">
        <span className="pagination-info">
          {total != null
            ? `Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`
            : `Page ${page}`}
        </span>
        <div className="pagination-controls">
          <button className="ent-btn sm" onClick={() => onPage(1)} disabled={page === 1}>«</button>
          <button className="ent-btn sm" onClick={() => onPage(page - 1)} disabled={page === 1}>{t('reports.reportTable.prev')}</button>
          <button
            className="ent-btn sm"
            onClick={() => onPage(page + 1)}
            disabled={totalPages != null ? page >= totalPages : !hasMore}
          >
            {t('reports.reportTable.next')}
          </button>
          {totalPages != null && (
            <button className="ent-btn sm" onClick={() => onPage(totalPages)} disabled={page >= totalPages}>»</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportTable;
